// Single controlled Socket.IO connection for the whole collaborative table-session feature.
//
// Why this exists: TableEntryPage.jsx and Checkout.jsx used to each call the `Socket()` factory
// (src/utils/Socket.config.jsx) directly inside their own effects, opening a brand new socket.io
// connection per component mount and calling socket.disconnect() on unmount. Two components visited
// in the same browsing session meant two separate handshakes to the same server. This module is the
// replacement: one connection, owned exclusively by TableSessionProvider (context/TableSessionContext.jsx).
// Nothing else should call connect/disconnect here - child components read state off the context.
//
// Auth: the backend (src/sockets.js#joinTableSessionRoom on smartpay-dashboard) resolves the
// table_session room purely from the `smartpay_table_participant` / legacy `smartpay_table_session`
// cookies during the connection handshake. It never reads socket.handshake.query for this purpose,
// so nothing session-identifying is (or should be) sent as a query param here - passing a
// session_uuid would just be ignored by the server and would be the wrong thing to trust anyway.
import io from "socket.io-client";

let socket = null;
const stateListeners = new Set();

const getServerUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  return apiUrl.split("/api")[0] || window.location.origin;
};

const notifyState = (state, detail) => {
  stateListeners.forEach((listener) => {
    try {
      listener(state, detail);
    } catch (err) {
      // A listener throwing must never break the socket's own event handling.
    }
  });
};

// Idempotent: safe to call every time TableSessionProvider's effect re-runs (e.g. status flips
// between APPROVED/ACTIVE) - returns the existing instance instead of opening a second one.
// Machine routing is not an authorization mechanism: table access remains cookie-based. These
// values only join the established machine room so this same singleton can hear the final-order
// event emitted by the /order flow.
export const connectTableSessionSocket = ({ machineCode, branchofficeCode, businessCode } = {}) => {
  if (socket) {
    if (!socket.connected) socket.connect();
    return socket;
  }

  socket = io(getServerUrl(), {
    // Netlify's reverse-proxy redirect (see public/_redirects) that fronts the Heroku backend
    // does not support WebSocket upgrades - polling-only keeps this same-origin so Safari's ITP
    // stops dropping the session cookies, at the cost of real-time push becoming long-polling.
    transports: ["polling"],
    withCredentials: true,
    autoConnect: true,
    query: machineCode && branchofficeCode && businessCode
      ? { machine_code: machineCode, branchoffice_code: branchofficeCode, business_code: businessCode }
      : undefined,
  });

  socket.on("connect", () => notifyState("connect"));
  socket.on("disconnect", (reason) => notifyState("disconnect", reason));
  socket.on("reconnect", (attempt) => notifyState("reconnect", attempt));
  socket.on("connect_error", (err) => notifyState("connect_error", err));

  return socket;
};

export const getTableSessionSocket = () => socket;

export const onTableSessionSocketState = (listener) => {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
};

// Only TableSessionProvider should call this (session ended, or the provider itself unmounts) -
// never from a child component. Removes every listener so a stray reference can't keep receiving
// events after the session is over, then tears down the transport.
export const disconnectTableSessionSocket = () => {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
};

export const isTableSessionSocketConnected = () => Boolean(socket?.connected);
